import { formatCurrency } from "./util/formatCurrency";

// Prefix match covers both renders of the form's x-data (legacy
// `autopayCardForm()` and the BT-enabled
// `autopayCardForm({...})`). Distinct x-data name from the one-time
// `paymentCardForm` component.
const FORM_SELECTOR = 'form[x-data^="autopayCardForm"]';
const SURCHARGE_DEBOUNCE_MS = 500;
// BT tokens are always "complete" — tokenize-on-blur only writes them
// once the Element reports valid. A raw PAN still needs a length gate
// to avoid firing on every keystroke partway through card entry.
const MIN_PAN_LENGTH = 13;

function readCardIdentifier(formEl) {
    const tokenInput = formEl.querySelector('input[name="card_token"]');
    if (tokenInput && tokenInput.value) {
        return { kind: 'token', value: tokenInput.value };
    }
    const panInput = formEl.querySelector('input[name*="[number]"]');
    if (panInput && panInput.value) {
        return { kind: 'pan', value: panInput.value };
    }
    return null;
}

export default function autopaySurcharge({
    pastDueAmount = 0,
    lateFeeAmount = 0,
    financeChargeAmount = 0,
    surchargeUrl = '',
    selected = 'bank_account',
}) {
    return {
        selected,

        pastDueAmount,
        lateFeeAmount,
        financeChargeAmount,
        surchargeUrl,

        cardNumberEntered: false,

        surchargeAmount: 0,
        isLoadingSurcharge: false,
        surchargeFetched: false,
        surchargeApplicableConfirmed: false,

        surchargeTimeout: null,
        surchargeAbortController: null,

        get totalToPay() {
            return this.pastDueAmount + this.lateFeeAmount + this.financeChargeAmount + this.surchargeAmount;
        },

        formatCurrency,

        init() {
            this.$watch('selected', (value) => {
                if (value === 'credit_card') {
                    this.$nextTick(() => this.watchCardNumberInput());
                } else {
                    // Surcharge is card-only; clearing on switch away
                    // keeps `totalToPay` honest if anything outside the
                    // credit-card x-if ever reads it.
                    this.resetSurcharge();
                }
            });

            if (this.selected === 'credit_card') {
                this.$nextTick(() => this.watchCardNumberInput());
            }
        },

        watchCardNumberInput() {
            const form = this.$el.querySelector(FORM_SELECTOR);
            if (!form) return;

            // _surchargeWatched guards against listener stacking if
            // init runs twice (bfcache, tab-switch toggle).
            const cardTokenInput = form.querySelector('input[name="card_token"]');
            if (cardTokenInput && !cardTokenInput._surchargeWatched) {
                cardTokenInput._surchargeWatched = true;
                cardTokenInput.addEventListener('input', () => this.onCardIdentifierChange(form));
            }

            const cardNumberInput = form.querySelector('input[name*="[number]"]');
            if (cardNumberInput && !cardNumberInput._surchargeWatched) {
                cardNumberInput._surchargeWatched = true;
                cardNumberInput.addEventListener('input', () => this.onCardIdentifierChange(form));
            }
        },

        onCardIdentifierChange(form) {
            clearTimeout(this.surchargeTimeout);

            const identifier = readCardIdentifier(form);
            if (!identifier || (identifier.kind === 'pan' && identifier.value.length < MIN_PAN_LENGTH)) {
                this.cardNumberEntered = false;
                this.resetSurcharge();
                return;
            }

            this.cardNumberEntered = true;

            this.surchargeTimeout = setTimeout(() => this.fetchSurcharge(form), SURCHARGE_DEBOUNCE_MS);
        },

        async fetchSurcharge(cardForm) {
            if (!this.surchargeUrl) {
                return;
            }

            const identifier = readCardIdentifier(cardForm);
            if (!identifier || (identifier.kind === 'pan' && identifier.value.length < MIN_PAN_LENGTH)) {
                this.resetSurcharge();
                return;
            }

            // Cancel any in-flight surcharge call so a late response
            // from a prior card/amount can't overwrite the current one.
            this.surchargeAbortController?.abort();
            this.surchargeAbortController = new AbortController();

            this.isLoadingSurcharge = true;

            try {
                const formData = new FormData(cardForm);
                const response = await fetch(this.surchargeUrl, {
                    method: 'POST',
                    body: formData,
                    signal: this.surchargeAbortController.signal,
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch surcharge');
                }

                const data = await response.json();
                if (data.error) {
                    throw new Error(data.error);
                }

                this.surchargeAmount = parseFloat(data.surcharge) || 0;
                this.surchargeFetched = true;
                this.surchargeApplicableConfirmed = data.surchargeApplicable ?? this.surchargeAmount > 0;
            } catch (error) {
                if (error.name === 'AbortError') {
                    return;
                }
                console.error('Failed to fetch surcharge:', error);
                this.surchargeFetched = false;
                this.surchargeAmount = 0;
            } finally {
                this.isLoadingSurcharge = false;
            }
        },

        resetSurcharge() {
            clearTimeout(this.surchargeTimeout);
            this.surchargeTimeout = null;
            this.surchargeAbortController?.abort();
            this.surchargeAbortController = null;
            this.surchargeAmount = 0;
            this.surchargeFetched = false;
            this.surchargeApplicableConfirmed = false;
            this.cardNumberEntered = false;
        }
    };
}
